import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { App, Button, Drawer, Form, Input, InputNumber, Select, Switch, Tooltip, type ButtonProps } from "antd";
import { X } from "@phosphor-icons/react";
import AppFeedback, { AppFeedbackBridge } from "../AppFeedback.tsx";
import { PendingRing } from "../LoadingRing.jsx";
import { t, getDirection } from "../../../features/store-language/i18n.ts";
import PortalDialog from "../PortalDialog.tsx";
import "./account-ui.css";

type Decision = { title: string; description: string; danger?: boolean; confirmLabel?: string };
const DecisionContext = createContext<(decision: Decision) => Promise<boolean>>(async function () { return false; });

export function AccountUIProvider({ children }: { children: ReactNode }) {
  const root = useRef<HTMLDivElement>(null);
  const [decision, setDecision] = useState<Decision>();
  const resolve = useRef<(confirmed: boolean) => void>();
  const confirm = useCallback(function (next: Decision) {
    resolve.current?.(false);
    setDecision(next);
    return new Promise<boolean>(function (complete) { resolve.current = complete; });
  }, []);
  function finish(confirmed: boolean) {
    resolve.current?.(confirmed);
    resolve.current = undefined;
    setDecision(undefined);
  }
  useEffect(function () { return function () { resolve.current?.(false); }; }, []);
  return (
    <div
      ref={root}
      className="account-ui"
    >
      <App
        className="account-ui__application"
        notification={{ getContainer: function () { return root.current || document.body; } }}
      >
        <AppFeedbackBridge />
        <DecisionContext.Provider value={confirm}>
          {children}
          <PortalDialog
            open={Boolean(decision)}
            title={decision?.title}
            destructive={decision?.danger}
            onClose={function () { finish(false); }}
            footer={
              <div className="account-ui__actions">
                <AccountButton
                  type="primary"
                  danger={decision?.danger}
                  onClick={function () { finish(true); }}
                >{decision?.confirmLabel || t("تأیید")}</AccountButton>
                <AccountButton data-dialog-cancel onClick={function () { finish(false); }}>{t("انصراف")}</AccountButton>
              </div>
            }
          >
            <p>{decision?.description}</p>
          </PortalDialog>
        </DecisionContext.Provider>
      </App>
    </div>
  );
}

export function useAccountFeedback() {
  const confirm = useContext(DecisionContext);
  return { confirm, success: AppFeedback.success, error: AppFeedback.error, httpError: AppFeedback.httpError };
}

export function AccountButton({ children, loading, ...props }: ButtonProps) {
  return (
    <Button
      {...props}
      className={`account-ui-button ${props.className || ""}`}
      disabled={props.disabled || Boolean(loading)}
      aria-busy={Boolean(loading)}
      icon={loading ? <PendingRing /> : props.icon}
    >{children}</Button>
  );
}

export function AccountIconButton({ label, ...props }: ButtonProps & { label: string }) {
  return (
    <Tooltip title={label}>
      <span>
        <AccountButton {...props}>
          <span className="platform-admin__sr-only">{label}</span>
        </AccountButton>
      </span>
    </Tooltip>
  );
}

export function AccountDrawer({ title, description, open, onClose, footer, children, wide = false, busy = false, dirty = false,
  placement, width, headerActions, flush = false, initialFocusId }: {
  title: ReactNode; description?: string; open: boolean; onClose: () => void | Promise<boolean>; footer?: ReactNode;
  children: ReactNode; wide?: boolean; busy?: boolean; dirty?: boolean;
  placement?: "left" | "right"; width?: string; headerActions?: ReactNode; flush?: boolean;
  initialFocusId?: string;
}) {
  useEffect(function () {
    if (!open || (!dirty && !busy)) return;
    const shell = document.querySelector<HTMLElement>(".customer-panel");
    async function guardNavigation(event: MouseEvent) {
      const target = (event.target as Element)?.closest<HTMLElement>("a[href], .platform-admin__nav-item, .raj-brand");
      if (!target || target.closest(".account-ui-drawer") || target.getAttribute("aria-disabled") === "true") return;
      event.preventDefault();
      event.stopPropagation();
      if (busy) return;
      if (await onClose() === true) {
        shell?.removeEventListener("click", guardNavigation, true);
        target.click();
      }
    }
    shell?.addEventListener("click", guardNavigation, true);
    return function () { shell?.removeEventListener("click", guardNavigation, true); };
  }, [open, dirty, busy, onClose]);
  return (
    <Drawer
      title={title}
      open={open}
      afterOpenChange={function (visible) {
        if (visible && initialFocusId) document.getElementById(initialFocusId)?.focus();
      }}
      placement={placement || (getDirection() === "rtl" ? "right" : "left")}
      rootClassName={`account-ui-drawer${wide ? " account-ui-drawer--wide" : ""}${flush ? " account-ui-drawer--flush" : ""}`}
      width={width || (wide ? "min(70rem, 100%)" : "min(32rem, 100%)")}
      getContainer={function () { return document.querySelector<HTMLElement>(".customer-panel, .platform-admin") || document.body; }}
      closeIcon={false}
      extra={<div className="account-ui__actions">{headerActions}<AccountIconButton
        label={t("بستن")}
        type="text"
        icon={<X size={18} />}
        disabled={busy}
        onClick={onClose}
      /></div>}
      keyboard={!busy}
      maskClosable={!busy}
      onClose={function () { if (!busy) onClose(); }}
      footer={footer ? <div className="account-ui__actions">{footer}</div> : undefined}
      destroyOnClose
    >
      {description ? <p className="account-ui__description">{description}</p> : null}
      {children}
    </Drawer>
  );
}

export function AccountSection({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="account-ui-section">
      <h2>{title}</h2>
      {children}
    </section>
  );
}

// Vendor controls remain behind the account's shared, themed form boundary.
export const AccountForm = Form;
export const AccountInput = Input;
export const AccountInputNumber = InputNumber;
export const AccountSelect = Select;
export const AccountSwitch = Switch;
// Grid toolbar uses the exact Button consumed by account/orders; no extra sizing layer.
export const AccountToolbarButton = Button;

const pendingActions = new Set<string>();

export function useAccountAction(key: string) {
  const mounted = useRef(true);
  const [pending, setPending] = useState(false);
  useEffect(function () {
    mounted.current = true;
    return function () { mounted.current = false; };
  }, []);
  async function run(action: () => Promise<void>, options?: { background?: boolean }) {
    if (pendingActions.has(key)) return;
    pendingActions.add(key);
    const trackPending = !options?.background;
    if (trackPending) setPending(true);
    try { await action(); }
    catch (error) { AppFeedback.httpError(error); }
    finally {
      pendingActions.delete(key);
      if (mounted.current && trackPending) setPending(false);
    }
  }
  return { pending, run };
}
